"use client";

/**
 * Services Section V2 - Modern card grid with enhanced UI
 */

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatCurrency, formatDuration } from "@/lib/tenant-landing-v2-utils";
import type { ServiceInfo } from "@/types/tenant-landing-v2";
import { Clock, Search, Sparkles } from "lucide-react";
import Image from "next/image";
import { useMemo, useState } from "react";

interface ServicesV2Props {
  services: ServiceInfo[];
  onServiceClick?: (service: ServiceInfo) => void;
}

export function ServicesV2({ services, onServiceClick }: ServicesV2Props) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return services;

    const query = searchQuery.toLowerCase();
    return services.filter(
      (service) =>
        service.name.toLowerCase().includes(query) ||
        service.description?.toLowerCase().includes(query)
    );
  }, [services, searchQuery]);

  if (services.length === 0) return null;

  return (
    <section
      id="servicos"
      className="px-4 py-4 max-w-screen-xl mx-auto scroll-mt-16"
      aria-label="Serviços"
    >
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-semibold">Nossos Serviços</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Escolha o serviço ideal para você
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
          Oferecemos uma variedade de serviços com profissionais qualificados
        </p>

        {/* Search */}
        {services.length > 3 && (
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar serviços..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
          </div>
        )}
      </div>

      {/* Services Grid */}
      {filteredServices.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredServices.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              onClick={() => onServiceClick?.(service)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            Nenhum serviço encontrado para &quot;{searchQuery}&quot;
          </p>
        </div>
      )}
    </section>
  );
}

interface ServiceCardProps {
  service: ServiceInfo;
  index: number;
  onClick?: () => void;
}

function ServiceCard({ service, index, onClick }: ServiceCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <Card
      className="group relative overflow-hidden border-2 hover:border-primary/50 
                 transition-all duration-300 hover:shadow-xl cursor-pointer
                 animate-in fade-in slide-in-from-bottom-4"
      style={{
        animationDelay: `${index * 100}ms`,
        animationFillMode: "backwards",
      }}
      onClick={() => onClick?.()}
    >
      {/* Image */}
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {service.imageUrl && !imageError ? (
          <>
            <Image
              src={service.imageUrl}
              alt={service.name}
              fill
              className="object-cover group-hover:scale-110 transition-transform duration-500"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              onError={() => setImageError(true)}
            />
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </>
        ) : (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-primary/10 via-primary/5 to-background">
            <Sparkles className="w-12 h-12 text-primary/40" />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {service.hasVariablePrice && (
            <Badge className="bg-primary text-primary-foreground shadow-lg">
              Preço variável
            </Badge>
          )}
          {service.requiresBookingFee && service.bookingFeeAmount && (
            <Badge variant="secondary" className="shadow-lg">
              Taxa: {formatCurrency(service.bookingFeeAmount)}
            </Badge>
          )}
        </div>
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-bold line-clamp-2 flex-1 group-hover:text-primary transition-colors">
            {service.name}
          </CardTitle>
          <div className="text-right shrink-0">
            <div className="flex flex-col items-center gap-1 text-xl font-bold text-primary">
              <span className="text-xs text-muted-foreground">
                {service.hasVariablePrice ? "A partir de " : ""}
              </span>
              {formatCurrency(service.price)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {service.description && (
          <CardDescription className="text-sm leading-relaxed line-clamp-3">
            {service.description}
          </CardDescription>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 shrink-0" />
            <span>{formatDuration(service.duration)}</span>
          </div>

          {/* Hover indicator */}
          <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Clique para agendar →
          </span>
        </div>
      </CardContent>

      {/* Shine effect on hover */}
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </Card>
  );
}
